/**
 * Client-Side Security Monitoring
 * Detects and reports security events in real-time
 */

import { logger } from "./logger";

interface SecurityEvent {
  type: 'dom_manipulation' | 'script_injection' | 'auth_failure' | 'suspicious_activity' | 'xss_attempt';
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: Record<string, any>;
  timestamp: number;
  url: string;
  userAgent: string;
}

class SecurityMonitor {
  private observers: Set<(event: SecurityEvent) => void> = new Set();
  private isActive = false;
  private mutationObserver: MutationObserver | null = null;
  private originalAddEventListener: typeof Element.prototype.addEventListener;

  /**
   * Start security monitoring
   */
  start(): void {
    if (this.isActive) return;
    this.isActive = true;

    this.setupDOMMonitoring();
    this.setupScriptInjectionDetection();
    this.setupErrorEventMonitoring();
    this.startBehavioralAnalysis();

    logger.info('🛡️ Security monitoring started (including behavioral analysis)');
  }

  /**
   * Stop security monitoring
   */
  stop(): void {
    if (!this.isActive) return;
    this.isActive = false;

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Clear behavioral analysis intervals
    this.behavioralIntervals.forEach(interval => clearInterval(interval));
    this.behavioralIntervals.clear();

    logger.info('🛡️ Security monitoring stopped (including behavioral analysis)');
  }

  /**
   * Setup DOM manipulation monitoring
   */
  private setupDOMMonitoring(): void {
    // Monitor for suspicious DOM changes
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check for script tag insertions
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.tagName === 'SCRIPT' && !this.isLegitimateScript(element)) {
                this.reportEvent({
                  type: 'script_injection',
                  severity: 'critical',
                  details: {
                    tagName: element.tagName,
                    src: element.getAttribute('src'),
                    innerHTML: element.innerHTML?.substring(0, 100)
                  }
                });
              }
            }
          });
        }

        // Check for attribute changes that could indicate XSS
        if (mutation.type === 'attributes') {
          const element = mutation.target as Element;
          const attributeName = mutation.attributeName;
          const newValue = element.getAttribute(attributeName || '');

          if (attributeName && this.isSuspiciousAttribute(attributeName, newValue)) {
            this.reportEvent({
              type: 'xss_attempt',
              severity: 'high',
              details: {
                element: element.tagName,
                attribute: attributeName,
                value: newValue?.substring(0, 100)
              }
            });
          }
        }
      }
    });

    this.mutationObserver.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'href', 'innerHTML', 'outerHTML', 'onload', 'onerror']
    });
  }

  /**
   * Check if a script element is legitimate
   */
  private isLegitimateScript(script: Element): boolean {
    const src = script.getAttribute('src');

    // Allow scripts from known domains
    if (src) {
      const allowedDomains = [
        'www.google-analytics.com',
        'js.newrelic.com',
        'www.googleadservices.com',
        'fundingchoicesmessages.google.com'
      ];

      try {
        const url = new URL(src);
        return allowedDomains.some(domain => url.hostname.includes(domain));
      } catch {
        return false;
      }
    }

    // Allow inline scripts that are known safe (add hashes of legitimate scripts)
    const content = script.innerHTML;
    return content.includes('dataLayer') || content.includes('gtag') || content.includes('New Relic');
  }

  /**
   * Check if an attribute change is suspicious
   */
  private isSuspiciousAttribute(name: string, value: string | null): boolean {
    if (!value) return false;

    // Check for event handler attributes
    if (name.startsWith('on')) {
      return value.includes('javascript:') || value.includes('eval(') || value.includes('Function(');
    }

    // Check for script content in HTML attributes
    if (name === 'innerHTML' || name === 'outerHTML') {
      return value.includes('<script') || value.includes('javascript:') || value.includes('onload=');
    }

    // Check for suspicious href values
    if (name === 'href' || name === 'src') {
      return value.includes('javascript:') || value.includes('data:text/html');
    }

    return false;
  }

  /**
   * Setup script injection detection via prototype monitoring
   */
  private setupScriptInjectionDetection(): void {
    // Monitor for attempts to modify document.write, eval, etc.
    const originalEval = window.eval;
    const originalWrite = document.write;
    const originalWriteln = document.writeln;

    // Override eval to detect suspicious usage
    window.eval = function(code: string) {
      logger.security('Eval usage detected', { code: code.substring(0, 100) });
      return originalEval.call(window, code);
    };

    // Monitor document.write usage
    document.write = function(content: string) {
      if (content.includes('<script') || content.includes('javascript:')) {
        logger.security('Suspicious document.write usage', { content: content.substring(0, 100) });
      }
      return originalWrite.call(document, content);
    };

    document.writeln = function(content: string) {
      if (content.includes('<script') || content.includes('javascript:')) {
        logger.security('Suspicious document.writeln usage', { content: content.substring(0, 100) });
      }
      return originalWriteln.call(document, content);
    };
  }

  /**
   * Setup error event monitoring for security-related errors
   */
  private setupErrorEventMonitoring(): void {
    // Listen for CSP violations
    document.addEventListener('securitypolicyviolation', (event) => {
      this.reportEvent({
        type: 'suspicious_activity',
        severity: 'high',
        details: {
          violatedDirective: event.violatedDirective,
          blockedURI: event.blockedURI,
          sourceFile: event.sourceFile,
          lineNumber: event.lineNumber
        }
      });
    });

    // Listen for unhandled promise rejections that might indicate attacks
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      if (reason && typeof reason === 'string') {
        if (reason.includes('Content Security Policy') || reason.includes('Refused to')) {
          this.reportEvent({
            type: 'suspicious_activity',
            severity: 'medium',
            details: { reason: reason.substring(0, 200) }
          });
        }
      }
    });
  }

  /**
   * Report a failed authentication attempt
   */
  reportAuthFailure(details: Record<string, any>): void {
    this.reportEvent({
      type: 'auth_failure',
      severity: 'medium',
      details
    });
  }

  /**
   * Report suspicious user activity
   */
  reportSuspiciousActivity(activity: string, details: Record<string, any>): void {
    this.reportEvent({
      type: 'suspicious_activity',
      severity: 'low',
      details: { activity, ...details }
    });
  }

  /**
   * Report a security event
   */
  private reportEvent(event: Omit<SecurityEvent, 'timestamp' | 'url' | 'userAgent'>): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Log locally
    logger.security(`Security event: ${event.type}`, event.details);

    // Send to monitoring service
    this.sendToMonitoring(fullEvent);

    // Notify observers
    this.observers.forEach(observer => {
      try {
        observer(fullEvent);
      } catch (error) {
        logger.error('Security monitor observer failed:', error, {});
      }
    });
  }

  /**
   * Send security event to monitoring service
   */
  private sendToMonitoring(event: SecurityEvent): void {
    const monitoringEndpoint = import.meta.env.VITE_SECURITY_MONITORING_ENDPOINT;
    if (!monitoringEndpoint) return;

    fetch(monitoringEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    }).catch(() => {
      // Silently fail if monitoring fails
    });
  }

  /**
   * Add an observer for security events
   */
  addObserver(observer: (event: SecurityEvent) => void): () => void {
    this.observers.add(observer);
    return () => this.observers.delete(observer);
  }

  /**
   * Behavioral analysis for detecting automated interactions
   */
  private behavioralMetrics = {
    mouseMoves: 0,
    keyPresses: 0,
    scrolls: 0,
    clicks: 0,
    touches: 0,
    lastActivity: Date.now(),
    sessionStart: Date.now(),
    rapidClicks: 0,
    predictablePatterns: 0
  };

  private behavioralIntervals: Set<NodeJS.Timeout> = new Set();
  private clickTimestamps: number[] = [];
  private scrollTimestamps: number[] = [];

  /**
   * Start behavioral analysis
   */
  private startBehavioralAnalysis(): void {
    // Mouse movement tracking
    const handleMouseMove = (event: MouseEvent) => {
      this.behavioralMetrics.mouseMoves++;
      this.behavioralMetrics.lastActivity = Date.now();
    };

    // Keyboard activity tracking
    const handleKeyPress = (event: KeyboardEvent) => {
      this.behavioralMetrics.keyPresses++;
      this.behavioralMetrics.lastActivity = Date.now();
    };

    // Scroll tracking
    const handleScroll = (event: Event) => {
      this.behavioralMetrics.scrolls++;
      this.behavioralMetrics.lastActivity = Date.now();
      this.scrollTimestamps.push(Date.now());

      // Keep only last 10 scroll events for analysis
      if (this.scrollTimestamps.length > 10) {
        this.scrollTimestamps.shift();
      }
    };

    // Click tracking with timing analysis
    const handleClick = (event: MouseEvent) => {
      this.behavioralMetrics.clicks++;
      this.behavioralMetrics.lastActivity = Date.now();
      this.clickTimestamps.push(Date.now());

      // Keep only last 20 click events for analysis
      if (this.clickTimestamps.length > 20) {
        this.clickTimestamps.shift();
      }
    };

    // Touch tracking for mobile
    const handleTouch = (event: TouchEvent) => {
      this.behavioralMetrics.touches++;
      this.behavioralMetrics.lastActivity = Date.now();
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('keypress', handleKeyPress, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick, { passive: true });
    document.addEventListener('touchstart', handleTouch, { passive: true });

    // Periodic behavioral analysis
    const behavioralAnalysisInterval = setInterval(() => {
      this.analyzeBehavioralPatterns();
    }, 30000); // Analyze every 30 seconds

    this.behavioralIntervals.add(behavioralAnalysisInterval);

    // Store cleanup functions
    this.observers.add(() => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouch);
    });

    logger.info('🧠 Behavioral analysis started');
  }

  /**
   * Analyze behavioral patterns to detect automation
   */
  private analyzeBehavioralPatterns(): void {
    const now = Date.now();
    const sessionDuration = now - this.behavioralMetrics.sessionStart;
    const timeSinceLastActivity = now - this.behavioralMetrics.lastActivity;

    // Reset rapid clicks counter
    this.behavioralMetrics.rapidClicks = 0;

    // Analyze click patterns (detect rapid clicking)
    if (this.clickTimestamps.length >= 5) {
      const recentClicks = this.clickTimestamps.slice(-10);
      let rapidClickCount = 0;

      for (let i = 1; i < recentClicks.length; i++) {
        const timeDiff = recentClicks[i] - recentClicks[i - 1];
        if (timeDiff < 100) { // Less than 100ms between clicks
          rapidClickCount++;
        }
      }

      if (rapidClickCount >= 3) {
        this.behavioralMetrics.rapidClicks = rapidClickCount;
        this.reportEvent({
          type: 'suspicious_activity',
          severity: 'medium',
          details: {
            activity: 'rapid_clicking',
            rapidClickCount,
            timeWindow: '10 clicks',
            pattern: 'Clicks faster than humanly possible'
          }
        });
      }
    }

    // Analyze scroll patterns (detect robotic scrolling)
    if (this.scrollTimestamps.length >= 5) {
      const recentScrolls = this.scrollTimestamps.slice(-5);
      let uniformScrollPattern = 0;

      for (let i = 1; i < recentScrolls.length; i++) {
        const timeDiff = recentScrolls[i] - recentScrolls[i - 1];
        if (timeDiff >= 900 && timeDiff <= 1100) { // Very uniform timing (1 second ± 100ms)
          uniformScrollPattern++;
        }
      }

      if (uniformScrollPattern >= 3) {
        this.behavioralMetrics.predictablePatterns++;
        this.reportEvent({
          type: 'suspicious_activity',
          severity: 'low',
          details: {
            activity: 'predictable_scrolling',
            uniformScrollPattern,
            pattern: 'Uniform scroll timing detected'
          }
        });
      }
    }

    // Check for lack of activity (but not too strict to avoid false positives)
    if (sessionDuration > 300000 && timeSinceLastActivity > 300000) { // 5 minutes session, no activity for 5 minutes
      if (this.behavioralMetrics.mouseMoves < 10 && this.behavioralMetrics.keyPresses < 5) {
        this.reportEvent({
          type: 'suspicious_activity',
          severity: 'low',
          details: {
            activity: 'low_interaction',
            sessionDuration,
            timeSinceLastActivity,
            mouseMoves: this.behavioralMetrics.mouseMoves,
            keyPresses: this.behavioralMetrics.keyPresses,
            pattern: 'Very low user interaction for session duration'
          }
        });
      }
    }

    // Log behavioral summary every 5 minutes
    if (Math.floor(sessionDuration / 300000) % 1 === 0) {
      logger.debug('📊 Behavioral analysis summary', {
        sessionDuration: Math.round(sessionDuration / 1000),
        mouseMoves: this.behavioralMetrics.mouseMoves,
        keyPresses: this.behavioralMetrics.keyPresses,
        scrolls: this.behavioralMetrics.scrolls,
        clicks: this.behavioralMetrics.clicks,
        touches: this.behavioralMetrics.touches,
        rapidClicks: this.behavioralMetrics.rapidClicks,
        predictablePatterns: this.behavioralMetrics.predictablePatterns
      });
    }
  }

  /**
   * Get current behavioral metrics
   */
  getBehavioralMetrics() {
    return { ...this.behavioralMetrics };
  }

  /**
   * Get monitoring status
   */
  isMonitoring(): boolean {
    return this.isActive;
  }
}

// Singleton instance
export const securityMonitor = new SecurityMonitor();

// Auto-start in production
if (import.meta.env.MODE === 'production') {
  securityMonitor.start();
}

export default securityMonitor;
